import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { investmentReadinessKeys } from '@/hooks/useInvestmentReadiness';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface KycStatus {
  state:          string;
  level?:         string;
  expires_at?:    string;
  submitted_at?:  string;
  validated_at?:  string;
  document_count?: number;
  progress?: KycProgress;
}

export interface KycProgress {
  personalInfo:   boolean;
  identity:       boolean;
  selfie:         boolean;
  proofOfAddress: boolean;
  sourceOfFunds:  boolean;
}

const KYC_PROGRESS_ORDER: Array<keyof KycProgress> = [
  'personalInfo',
  'identity',
  'selfie',
  'proofOfAddress',
  'sourceOfFunds',
];

export function getKycAccessibleStepIndex(progress?: KycProgress): number {
  if (!progress) return 0;
  const firstIncomplete = KYC_PROGRESS_ORDER.findIndex(key => !progress[key]);
  return firstIncomplete === -1 ? KYC_PROGRESS_ORDER.length : firstIncomplete;
}

export interface KycCase {
  uuid:  string;
  state: string;
  name:  string;
}

export interface UploadDocumentInput {
  caseUuid:     string;
  documentType: string;
  file:         File;
}

// ─── Query keys ───────────────────────────────────────────────────────────────

export const kycKeys = {
  status: ['kyc', 'status'] as const,
  case:   ['kyc', 'case']   as const,
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useKycStatus() {
  return useQuery({
    queryKey: kycKeys.status,
    queryFn:  () => apiClient.get<KycStatus>('/kyc/status'),
    staleTime: 30 * 1000,
  });
}

export function useStartKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post<KycCase>('/kyc/start'),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: kycKeys.status });
      qc.invalidateQueries({ queryKey: investmentReadinessKeys.current });
    },
  });
}

export function useSavePersonalInfo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      dateOfBirth:  string;
      nationality:  string;
      phone:        string;
      iban?:        string;
    }) => apiClient.post<{ updated: boolean }>('/kyc/personal-info', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kycKeys.status });
      qc.invalidateQueries({ queryKey: investmentReadinessKeys.current });
    },
  });
}

export function useSaveSourceOfFunds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { fundSource: string; monthlyIncome: string }) =>
      apiClient.post<{ saved: boolean }>('/kyc/source-of-funds', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kycKeys.status });
      qc.invalidateQueries({ queryKey: investmentReadinessKeys.current });
    },
  });
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseUuid, documentType, file }: UploadDocumentInput) => {
      const fd = new FormData();
      fd.append('caseUuid',     caseUuid);
      fd.append('documentType', documentType);
      fd.append('file',         file);
      return apiClient.upload<{ documentUuid: string; sha256: string }>('/kyc/upload-document', fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kycKeys.status });
      qc.invalidateQueries({ queryKey: investmentReadinessKeys.current });
    },
  });
}

export function useSubmitKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caseUuid: string) =>
      apiClient.post<{ submitted: boolean; state: string; mvpSimulated: boolean }>('/kyc/submit', { caseUuid }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kycKeys.status });
      qc.invalidateQueries({ queryKey: investmentReadinessKeys.current });
    },
  });
}
